import {
  HANDOVER_ITEM_MAX_CHARS,
  HANDOVER_LIST_MAX_ITEMS,
  HANDOVER_NOTES_MAX_CHARS,
  HandoverDocumentSchema,
  type HandoverDocument,
  type HandoverDocumentInput,
  type HandoverRepository,
  type RepositoryView,
} from "@baton/contracts";
import type { MessageContent } from "./messages.js";

/**
 * What the two composers hold while a handoff is being written. Lists are kept as raw
 * text (one item per line) until send time so typing never fights with normalisation.
 */
export interface HandoffDraftState {
  subject: string;
  criteria: string;
  done: string;
  todo: string;
  verify: string;
  ideas: string;
  notes: string;
  repositoryId: string | null;
  branch: string;
}

export interface ComposeInput {
  type: "chat" | "handoff";
  body: string;
  handoff: HandoffDraftState;
  project: { id: string; name: string } | null;
  /** The repository the sender picked; becomes the handover's single repository reference and the routing `repoId`. */
  repository: RepositoryView | null;
}

const BRANCH_MAX_CHARS = 255;

/** A blank draft; with exactly one repository to choose from, that repository is preselected on its default branch. */
export function emptyHandoffDraft(repositories: readonly RepositoryView[] = []): HandoffDraftState {
  const only = repositories.length === 1 ? repositories[0] : undefined;
  return {
    subject: "",
    criteria: "",
    done: "",
    todo: "",
    verify: "",
    ideas: "",
    notes: "",
    repositoryId: only?.id ?? null,
    branch: only?.defaultBranch ?? "",
  };
}

/** One item per line: trims each line and drops the blank ones. */
export function splitLines(text: string): string[] {
  return text.split(/\r?\n/).map((line) => line.trim()).filter((line) => line.length > 0);
}

/**
 * Turns a composer draft into the payload the worker encrypts. A chat carries only its
 * body. A handoff also carries subject and acceptance criteria as before, and a handover
 * document only when one of its lists, the notes or a repository was actually filled in,
 * so a plain handoff still encrypts to the legacy shape.
 */
export function buildMessageContent(input: ComposeInput): MessageContent {
  const body = input.body.trim();
  if (!body) throw new Error("Write a message first.");
  if (input.type !== "handoff") return { body };
  const content: MessageContent = { body };
  const subject = input.handoff.subject.trim();
  if (subject) content.subject = subject;
  const criteria = splitLines(input.handoff.criteria);
  if (criteria.length > 0) content.acceptanceCriteria = criteria;
  const handover = buildHandover(input);
  if (handover) content.handover = handover;
  return content;
}

const LIST_LABELS = { done: "Done", todo: "To do", verify: "Verify", ideas: "Ideas" } as const;

function buildHandover(input: ComposeInput): HandoverDocument | null {
  const draft = input.handoff;
  const lists = {
    done: splitLines(draft.done),
    todo: splitLines(draft.todo),
    verify: splitLines(draft.verify),
    ideas: splitLines(draft.ideas),
  };
  const notes = draft.notes.trim();
  const repository = input.repository;
  const empty = Object.values(lists).every((list) => list.length === 0) && !notes && !repository;
  if (empty) return null;

  for (const key of Object.keys(lists) as Array<keyof typeof lists>) {
    const label = LIST_LABELS[key];
    const list = lists[key];
    if (list.length > HANDOVER_LIST_MAX_ITEMS) {
      throw new Error(`${label} has ${list.length} items; the limit is ${HANDOVER_LIST_MAX_ITEMS}.`);
    }
    const long = list.find((item) => item.length > HANDOVER_ITEM_MAX_CHARS);
    if (long !== undefined) {
      throw new Error(`A ${label.toLowerCase()} item is longer than ${HANDOVER_ITEM_MAX_CHARS} characters.`);
    }
  }
  if (notes.length > HANDOVER_NOTES_MAX_CHARS) {
    throw new Error(`Notes are longer than ${HANDOVER_NOTES_MAX_CHARS} characters.`);
  }

  const document: HandoverDocumentInput = { version: 1, ...lists, repositories: [] };
  if (input.project) document.project = { id: input.project.id, name: input.project.name };
  if (repository) document.repositories = [repositoryReference(repository, draft.branch)];
  if (notes) document.notes = notes;

  const parsed = HandoverDocumentSchema.safeParse(document);
  if (!parsed.success) {
    const issue = parsed.error.issues[0];
    throw new Error(issue?.message.includes("32 KiB")
      ? "The handover is larger than 32 KiB; shorten the lists or the notes."
      : issue?.message ?? "The handover could not be built.");
  }
  return parsed.data;
}

/** The dashboard knows the branch the sender named but never a commit: only a checkout can vouch for one. */
function repositoryReference(repository: RepositoryView, branchInput: string): HandoverRepository {
  const branch = branchInput.trim() || repository.defaultBranch;
  if (branch.length > BRANCH_MAX_CHARS) throw new Error(`Branch is longer than ${BRANCH_MAX_CHARS} characters.`);
  return { repositoryId: repository.id, owner: repository.ownerHandle, name: repository.name, branch };
}
